// src/pages/LoginHelpPanel.tsx — "Help me sign in" inline helper.
//
// Rendered by LoginScreen (inside its AuthShell) when the user taps "Help me
// sign in". A gated flow for an ALREADY-APPROVED volunteer who can't get in on
// their own:
//
//   1. email   — they type their personal email → POST /api/login-help/start.
//               The gate is server-side and HARD: only a matched (pre-approved
//               or existing) email returns { eligible:true, token }. An unknown
//               email returns { eligible:false } and NEVER reaches the keypad or
//               chat — it lands on the "denied" step instead.
//   2. help    — (eligible only) a message confirming a code has been emailed,
//               then the SAME PIN keypad the main login uses (PinDots + NumPad),
//               which posts to the real /api/auth/login — the only thing that
//               signs anyone in. Beside it sits a small Claude (Haiku) helper
//               that coaches them to find + enter the code. The helper CANNOT
//               sign anyone in; it only talks.
//   3. denied  — (no match) a polite dead-end; no keypad, no chat.
//
// Reusing the real keypad is deliberate — it trains the person on the interface
// they'll use next time.

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { writeAuthHint } from "../auth";
import { C, F } from "../theme";
import { BackLink, Button, Eyebrow, Field, NumPad, PIN_LENGTH, PIN_REGEX, PinDots } from "../components/ui";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PIN_EMAIL_KEY = "nymr_pin_email";

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

const GREETING =
  "Hi — I'm here to help you sign in. A 6-character code has just been emailed to you. Type it into the keypad above and press OK. If you can't find it or get stuck, tell me what you can see and I'll help.";

function maskEmail(e: string): string {
  const at = e.indexOf("@");
  if (at <= 0) return e;
  const local = e.slice(0, at);
  const domain = e.slice(at + 1);
  return `${local.slice(0, 1)}${"•".repeat(Math.max(3, local.length - 1))}@${domain}`;
}

function formatLockedUntil(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export function LoginHelpPanel({ onExit }: { onExit: () => void }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"email" | "help" | "denied">("email");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  // Real PIN entry (posts to /api/auth/login — same as the standard login).
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSubmitting, setPinSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);

  // Claude helper.
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [closed, setClosed] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!shake) return;
    const t = setTimeout(() => setShake(false), 500);
    return () => clearTimeout(t);
  }, [shake]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  function exit() {
    if (token) void apiFetch("/api/login-help/close", { method: "POST", body: JSON.stringify({ token }) }).catch(() => {});
    onExit();
  }

  async function startHelp() {
    if (starting) return;
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(trimmed)) {
      setStartError("Enter a valid email address.");
      return;
    }
    setStarting(true);
    setStartError(null);
    try {
      const res = await apiFetch<{ eligible: boolean; token?: string }>("/api/login-help/start", {
        method: "POST",
        body: JSON.stringify({ email: trimmed }),
      });
      setEmail(trimmed);
      // HARD gate: only a matched email (eligible + token) reaches the keypad +
      // chat. Everything else lands on the polite dead-end.
      if (res.eligible && res.token) {
        setToken(res.token);
        setPhase("help");
      } else {
        setPhase("denied");
      }
    } catch (e: unknown) {
      setStartError(e instanceof ApiError ? e.message : "Couldn't reach the server. Try again.");
    } finally {
      setStarting(false);
    }
  }

  function pressChar(c: string) {
    if (pinSubmitting || locked) return;
    setPin((p) => (p.length >= PIN_LENGTH ? p : p + c));
  }
  function pressDelete() {
    if (pinSubmitting || locked) return;
    setPin((p) => p.slice(0, -1));
  }

  async function submitPin() {
    if (pinSubmitting || locked) return;
    if (!PIN_REGEX.test(pin)) {
      setPinError("Enter the 6-character code from your email (letter, number, letter, then 3 numbers).");
      setShake(true);
      return;
    }
    setPinSubmitting(true);
    try {
      await apiFetch<{ ok: true }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, pin }) });
      writeAuthHint("pin");
      localStorage.setItem(PIN_EMAIL_KEY, email);
      navigate("/", { replace: true });
    } catch (e: unknown) {
      const status = e instanceof ApiError ? e.status : 0;
      const body = e instanceof ApiError ? (e.body as Record<string, unknown> | null) : null;
      if (status === 423) {
        const until = typeof body?.lockedUntil === "string" ? body.lockedUntil : null;
        setLocked(true);
        setPinError(until ? `Too many wrong tries — locked until ${formatLockedUntil(until)}.` : "Account locked.");
        setPin("");
      } else if (status === 401) {
        const left = typeof body?.attemptsLeft === "number" ? body.attemptsLeft : null;
        setPinError(
          left && left > 0
            ? `That code didn't work. ${left} ${left === 1 ? "try" : "tries"} left — use the most recent code from your email.`
            : "That code didn't work.",
        );
        setShake(true);
        setPin("");
      } else if (status === 429) {
        setPinError("Too many attempts. Try again in a few minutes.");
        setPin("");
      } else {
        setPinError(e instanceof ApiError ? e.message : "Couldn't sign in. Try again.");
      }
      setPinSubmitting(false);
    }
  }

  async function sendChat() {
    const text = input.trim();
    if (!text || sending || closed || !token) return;
    const next: ChatMsg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const res = await apiFetch<{ reply: string; closed?: boolean }>("/api/login-help/chat", {
        method: "POST",
        body: JSON.stringify({ token, messages: next }),
      });
      setMessages([...next, { role: "assistant", content: res.reply }]);
      if (res.closed) setClosed(true);
    } catch {
      setMessages([
        ...next,
        { role: "assistant", content: "Sorry, I had trouble just then. You can still type the code from your email into the keypad above." },
      ]);
    } finally {
      setSending(false);
    }
  }

  // ── email step ────────────────────────────────────────────────────────────────────────
  if (phase === "email") {
    return (
      <div style={colStyle}>
        <Eyebrow style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontWeight: 800 }}>Sign-in help</Eyebrow>
        <h1 style={titleStyle}>Let's get you in</h1>
        <p style={subStyle}>Enter the personal email address you gave the railway and we'll email you a fresh code to sign in with.</p>
        <div style={{ width: "100%", maxWidth: 360 }}>
          <Field
            label="Your personal email address"
            value={email}
            onChange={setEmail}
            type="email"
            ph="you@example.com"
            disabled={starting}
          />
          {startError && (
            <div role="alert" style={errorStyle}>
              {startError}
            </div>
          )}
          <Button variant="primary" fullWidth onClick={startHelp} disabled={starting}>
            {starting ? "Checking…" : "Email me a code"}
          </Button>
          <BackLink onClick={exit} />
        </div>
      </div>
    );
  }

  // ── denied step (no matching account) ─────────────────────────────────────────────────────────
  if (phase === "denied") {
    return (
      <div style={colStyle}>
        <Eyebrow style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontWeight: 800 }}>Sign-in help</Eyebrow>
        <h1 style={titleStyle}>We can't find that email</h1>
        <p style={subStyle}>
          We don't recognise that email address on the Info Hub. If you're a volunteer who should have access, please contact the person who invited you (or the NYMR office) to be set up first — then come back and sign in.
        </p>
        <div style={{ width: "100%", maxWidth: 360 }}>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              setEmail("");
              setStartError(null);
              setPhase("email");
            }}
          >
            Try a different email
          </Button>
          <div style={{ marginTop: 10, textAlign: "center" }}>
            <BackLink onClick={exit} />
          </div>
        </div>
      </div>
    );
  }

  // ── help step (eligible only) ──────────────────────────────────────────────────────────────
  return (
    <div style={colStyle}>
      <Eyebrow style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", fontWeight: 800 }}>Enter your code</Eyebrow>
      <p style={{ ...subStyle, marginBottom: 16 }}>
        We've just emailed a 6-character code to <strong style={{ color: C.brassDk }}>{maskEmail(email)}</strong>. Enter it below to sign in.
      </p>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", width: "100%", maxWidth: 360 }}>
        <PinDots value={pin} shake={shake} />
        {pinError && (
          <div role="alert" style={errorStyle}>
            {pinError}
          </div>
        )}
        <NumPad length={pin.length} onPress={pressChar} onDelete={pressDelete} onSubmit={submitPin} disabled={pinSubmitting || locked} />
      </div>

      {/* Claude helper */}
      <div style={helpBoxStyle}>
        <div style={helpHeaderStyle}>
          <span style={{ fontFamily: F.serif, fontWeight: 700, fontSize: 14, color: C.ink }}>Sign-in helper</span>
          <span style={{ fontFamily: F.body, fontSize: 11, color: C.inkMute, fontWeight: 700 }}>here to help you get in</span>
        </div>
        <div ref={scrollRef} style={chatScrollStyle}>
          <Bubble role="assistant" text={GREETING} />
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} text={m.content} />
          ))}
          {sending && <Bubble role="assistant" text="…" />}
        </div>
        {closed ? (
          <div style={{ padding: "10px 12px", fontFamily: F.body, fontSize: 12.5, color: C.inkMute, fontStyle: "italic" }}>
            This chat has ended. You can still enter your code on the keypad above.
          </div>
        ) : (
          <div style={chatInputRowStyle}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void sendChat();
                }
              }}
              placeholder="Type your message…"
              disabled={sending}
              style={chatInputStyle}
            />
            <Button variant="primary" onClick={() => void sendChat()} disabled={sending || input.trim().length === 0} style={{ minHeight: 42, padding: "0 16px" }}>
              Send
            </Button>
          </div>
        )}
      </div>

      <div style={{ marginTop: 6 }}>
        <BackLink onClick={exit} />
      </div>
    </div>
  );
}

// ── small bubble ───────────────────────────────────────────────────────────────────────
function Bubble({ role, text }: { role: "user" | "assistant"; text: string }) {
  const isUser = role === "user";
  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 8 }}>
      <div
        style={{
          maxWidth: "82%",
          padding: "8px 12px",
          borderRadius: 14,
          background: isUser ? C.brassDk : C.paperWarm,
          color: isUser ? C.brassGlow : C.ink,
          fontFamily: F.body,
          fontSize: 13.5,
          fontWeight: 600,
          lineHeight: 1.45,
          whiteSpace: "pre-wrap",
          borderBottomRightRadius: isUser ? 4 : 14,
          borderBottomLeftRadius: isUser ? 14 : 4,
        }}
      >
        {text}
      </div>
    </div>
  );
}

// ── styles ───────────────────────────────────────────────────────────────────────
const colStyle: CSSProperties = {
  width: "100%",
  maxWidth: 440,
  padding: "28px 4px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const titleStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 26,
  fontWeight: 700,
  color: C.ink,
  margin: "4px 0 6px",
  letterSpacing: -0.6,
  textAlign: "center",
};

const subStyle: CSSProperties = {
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 14,
  color: C.inkSoft,
  marginTop: 0,
  marginBottom: 22,
  fontWeight: 600,
  textAlign: "center",
  maxWidth: 340,
  lineHeight: 1.5,
};

const errorStyle: CSSProperties = {
  padding: "8px 14px",
  borderRadius: 10,
  background: C.signalBg,
  border: "1px solid rgba(184,58,43,0.22)",
  color: C.signalDk,
  fontFamily: F.body,
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 12,
  textAlign: "center",
};

const helpBoxStyle: CSSProperties = {
  width: "100%",
  maxWidth: 360,
  marginTop: 22,
  border: `1px solid ${C.hairline}`,
  borderRadius: 16,
  background: C.card,
  boxShadow: C.shadowS,
  overflow: "hidden",
};

const helpHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  gap: 8,
  padding: "10px 14px",
  borderBottom: `1px solid ${C.hairline}`,
  background: C.brassBg,
};

const chatScrollStyle: CSSProperties = {
  maxHeight: 220,
  overflowY: "auto",
  padding: "12px 12px 4px",
};

const chatInputRowStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  padding: "10px 12px",
  borderTop: `1px solid ${C.hairline}`,
  alignItems: "center",
};

const chatInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${C.hairline}`,
  background: C.paperSoft,
  fontFamily: F.body,
  fontSize: 14,
  fontWeight: 600,
  color: C.ink,
  outline: "none",
};
